<template>
  <div>
    <div class="card card-custom gutter-b">
      <div class="card-header border-0 py-5">
        <h3 class="card-title align-items-start flex-column">
          <span class="card-label font-weight-bolder text-dark">
            Central de Novidades
          </span>
          <span class="text-muted mt-3 font-weight-bold font-size-sm">
            Gerencie os pacotes de novidades do sistema
          </span>
        </h3>
        <div class="card-toolbar">
          <router-link
            :to="{ name: 'novidadesAdminNovo' }"
            class="btn btn-dark font-weight-bolder font-size-sm"
          >
            + Novo Pacote
          </router-link>
        </div>
      </div>

      <div class="card-body pt-0 pb-3">
        <div v-if="loading" class="text-center py-10">
          <b-spinner variant="primary" />
          <p class="mt-3 text-muted">Carregando pacotes...</p>
        </div>

        <div v-else-if="erro" class="alert alert-light-danger d-flex align-items-center justify-content-between p-4">
          <span>{{ erro }}</span>
          <button class="btn btn-sm btn-light-danger font-weight-bold" @click="carregar">
            Tentar novamente
          </button>
        </div>

        <div v-else class="table-responsive">
          <table class="table table-head-custom table-head-bg table-borderless table-vertical-center">
            <thead>
              <tr class="text-left text-uppercase">
                <th style="min-width: 200px">Título</th>
                <th style="min-width: 80px">Versão</th>
                <th style="min-width: 120px">Data publicação</th>
                <th style="min-width: 90px">Status</th>
                <th style="min-width: 80px">Destaque</th>
                <th style="min-width: 80px">Modal</th>
                <th style="min-width: 70px">Itens</th>
                <th style="min-width: 130px">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="pacotes.length === 0">
                <td colspan="8" class="text-center text-muted py-10">
                  Nenhum pacote cadastrado ainda.
                </td>
              </tr>
              <tr v-for="pacote in pacotes" :key="pacote.id">
                <td>
                  <span class="text-dark-75 font-weight-bolder d-block font-size-lg">
                    {{ pacote.titulo }}
                  </span>
                  <span class="text-muted font-weight-bold font-size-sm" v-if="pacote.descricao_curta">
                    {{ pacote.descricao_curta }}
                  </span>
                </td>
                <td>
                  <span class="text-muted font-weight-bold">
                    {{ pacote.versao || "—" }}
                  </span>
                </td>
                <td>
                  <span class="text-dark-75 font-weight-bold">
                    {{ formatDate(pacote.data_publicacao) }}
                  </span>
                </td>
                <td>
                  <span
                    class="label label-lg label-inline font-weight-bold"
                    :class="pacote.publicado ? 'label-light-success' : 'label-light-warning'"
                  >
                    {{ pacote.publicado ? "Publicado" : "Rascunho" }}
                  </span>
                </td>
                <td>
                  <span
                    class="label label-lg label-inline font-weight-bold"
                    :class="pacote.destacar_dashboard ? 'label-light-primary' : 'label-light-secondary'"
                  >
                    {{ pacote.destacar_dashboard ? "Sim" : "Não" }}
                  </span>
                </td>
                <td>
                  <span
                    class="label label-lg label-inline font-weight-bold"
                    :class="pacote.exibir_modal ? 'label-light-info' : 'label-light-secondary'"
                  >
                    {{ pacote.exibir_modal ? "Sim" : "Não" }}
                  </span>
                </td>
                <td>
                  <span class="font-weight-bolder text-dark-75">
                    {{ pacote.itens ? pacote.itens.length : 0 }}
                  </span>
                </td>
                <td class="pr-0 text-right">
                  <router-link
                    :to="{ name: 'novidadesAdminEditar', params: { id: pacote.id } }"
                    class="btn btn-icon btn-light btn-hover-primary btn-sm mr-1"
                    v-b-tooltip.hover title="Editar"
                  >
                    <Pencil :size="16" />
                  </router-link>

                  <button
                    class="btn btn-icon btn-sm mr-1"
                    :class="pacote.publicado ? 'btn-light btn-hover-warning' : 'btn-light btn-hover-success'"
                    v-b-tooltip.hover
                    :title="pacote.publicado ? 'Voltar para rascunho' : 'Publicar'"
                    @click="togglePublicado(pacote)"
                  >
                    <Check :size="16" v-if="!pacote.publicado" />
                    <EyeOff :size="16" v-else />
                  </button>

                  <button
                    class="btn btn-icon btn-light btn-hover-danger btn-sm"
                    v-b-tooltip.hover title="Excluir"
                    @click="excluir(pacote)"
                  >
                    <TrashCan :size="16" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { SistemaMixin } from "@/modules/Application";
import { mapGetters } from "vuex";
import Pencil from "vue-material-design-icons/Pencil.vue";
import Check from "vue-material-design-icons/Check.vue";
import EyeOff from "vue-material-design-icons/EyeOff.vue";
import TrashCan from "vue-material-design-icons/TrashCan.vue";

export default {
  name: "app-novidades-admin",
  mixins: [SistemaMixin],
  components: {
    Pencil,
    Check,
    EyeOff,
    TrashCan,
  },
  data() {
    return {
      loading: false,
      erro: "",
      pacotes: [],
    };
  },
  mounted() {
    if (!this.isNuts) {
      this.$router.push({ name: "dashboard" });
      return;
    }
    this.$store.dispatch("setLoading", false);
    this.carregar();
  },
  methods: {
    async carregar() {
      this.loading = true;
      this.erro = "";
      try {
        const { data } = await this.$http.post("/novidades/admin/");
        this.pacotes = data.data || data || [];
      } catch (error) {
        this.erro =
          error?.response?.data?.msg ||
          error?.response?.data?.message ||
          "Não foi possível carregar os pacotes de novidades.";
      } finally {
        this.loading = false;
      }
    },

    async togglePublicado(pacote) {
      try {
        if (pacote.publicado) {
          await this.$http.post("/novidades/admin/rascunho", { id: pacote.id });
          this.$toast.success("Pacote movido para rascunho.");
        } else {
          await this.$http.post("/novidades/admin/publicar", { id: pacote.id });
          this.$toast.success("Pacote de novidades publicado com sucesso.");
        }
        this.carregar();
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.msg ||
          error?.response?.data?.message ||
          "Não foi possível alterar o status do pacote."
        );
      }
    },

    async excluir(pacote) {
      const result = await this.$swal.fire({
        icon: "warning",
        title: "Excluir este pacote?",
        text: `O pacote "${pacote.titulo}" será removido permanentemente.`,
        showCancelButton: true,
        confirmButtonText: "Sim, excluir",
        cancelButtonText: "Cancelar",
      });

      if (!result.isConfirmed) return;

      try {
        await this.$http.post("/novidades/admin/delete", { id: pacote.id });
        this.$toast.success("Pacote de novidades removido com sucesso.");
        this.carregar();
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.msg ||
          error?.response?.data?.message ||
          "Não foi possível excluir o pacote."
        );
      }
    },

    formatDate(date) {
      if (!date) return "—";
      return this.$moment(date).format("DD/MM/YYYY");
    },
  },
  computed: {
    ...mapGetters(["isNuts"]),
  },
};
</script>
